import React, { useEffect } from 'react'
import { Box, Typography, Select, MenuItem, TextField, InputAdornment, Divider, Button, CircularProgress } from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import PriceBox from './price-box'
import PCILogo from '../../../../components/logos/pci.png'
import { b2cSignupSlice } from '../../../../reducers'
import { useDispatch, useSelector } from 'react-redux'
import { getFormattedDuration, formatAmount } from '../utils'
import { useCheckoutPricing } from '@recurly/react-recurly'
import { useFormFactor } from '../../../../hooks'
import { CREATE_ACCOUNT_STEP } from '../progress-bar'
import useStyles from './styles'
import { PaymentHeading } from '../payment/heading'
import { TermsOfServiceLink } from '../../../../components/policy-links'

const OrderSummary = () => {
  const dispatch = useDispatch()
  const sx = useStyles()
  const plan = useSelector(b2cSignupSlice.selectors.selectPlan)
  const availablePlans = useSelector(b2cSignupSlice.selectors.selectAvailablePlans)
  const [coupon, setCoupon] = React.useState('')
  const [showCouponMessage, setShowCouponMessage] = React.useState(false)
  const [, setRecurlyError] = React.useState('')
  const [{ price, loading } = {}, setCheckoutPricing = () => {}] = useCheckoutPricing({}, setRecurlyError)
  const [isLoading, setIsLoading] = React.useState(false)
  const paymentInfoSubmitted = useSelector(b2cSignupSlice.selectors.selectPaymentInfoSubmitted)
  const { isSmallTablet, isMobile } = useFormFactor()
  const currentStep = useSelector(b2cSignupSlice.selectors.selectCurrentStep)
  const defaultPlan = useSelector(b2cSignupSlice.selectors.selectDefaultPlan)
  const disabled = useSelector(b2cSignupSlice.selectors.selectDisabled)

  useEffect(() => {
    setIsLoading(!plan || !price)
  }, [plan, price])

  useEffect(() => {
    dispatch(b2cSignupSlice.actions.setCheckoutPricing(price))
  }, [price, dispatch])

  useEffect(() => {
    if (plan?.price) {
      const order = {
        subscriptions: [
          {
            plan: plan.code
          }
        ],
        currency: plan.price.currency
      }
      setCheckoutPricing(order)
    }
  }, [plan, setCheckoutPricing])

  const handleInputChange = (event) => {
    setShowCouponMessage(false)
    const order = {
      subscriptions: [
        {
          plan: plan.code
        }
      ],
      currency: plan.price.currency
    }
    // clear the discount
    setCheckoutPricing(order)
    setCoupon(event.target.value)
  }

  const handleCouponChange = (e) => {
    if (plan?.price) {
      const order = {
        subscriptions: [
          {
            plan: plan.code
          }
        ],
        currency: plan.price.currency
      }
      setShowCouponMessage(true)

      // apply the discount
      if (coupon) order.coupon = coupon
      setCheckoutPricing(order)
    }
    dispatch(b2cSignupSlice.actions.setCoupon(coupon))
  }

  const handlePlanChange = (e) => {
    dispatch(b2cSignupSlice.actions.setPlan(availablePlans.data.find(plan => plan.code === e.target.value)))
  }

  const hideMobile = () => ((isSmallTablet || isMobile) && currentStep === CREATE_ACCOUNT_STEP)

  // use the plan code from the URL if it is a valid plan to use
  const defaultPlanCode = defaultPlan && availablePlans?.data?.find(plan => plan.code === defaultPlan) ? defaultPlan : plan?.code

  return (
    <>
      {!hideMobile() && (
        <>
          <Box className={sx.root}>
            <PaymentHeading hideMobile />
            <Box className={sx.summary}>
              <Typography variant='h6' fontWeight='bold' gutterBottom>
                Order Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {defaultPlanCode &&
                <>
                  <Typography fontWeight='bold' mb={1}>
                    Choose a plan
                  </Typography>

                  <Select defaultValue={defaultPlanCode} disabled={paymentInfoSubmitted || disabled} onChange={handlePlanChange} fullWidth variant='outlined' className={sx.textField}>
                    {availablePlans?.data && availablePlans?.data.map((plan, index) => (
                      <MenuItem key={index} value={plan.code}>
                        <Typography>
                          {plan.name} - {formatAmount(plan.price.currency, plan.price.unitAmount + (plan.price.setupFee ?? 0))}
                        </Typography>
                      </MenuItem>

                    ))}
                  </Select>
                </>}
              {/* Promo Code */}
              <TextField
                variant='outlined'
                fullWidth
                onBlur={handleInputChange}
                placeholder='Promo Code'
                className={sx.promoTextField}
                disabled={paymentInfoSubmitted || disabled}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleCouponChange() // Apply coupon on Enter key
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment
                      sx={{
                        width: '94px',
                        height: '100%'
                      }} position='end'
                    >
                      <Box display='flex' alignItems='center' justifyContent='center' width='100%' height='100%'>
                        <Button
                          onClick={handleCouponChange} className={sx.button} disabled={paymentInfoSubmitted || disabled}
                        >
                          Apply
                        </Button>
                      </Box>
                    </InputAdornment>
                  )
                }}
              />
              {showCouponMessage && (
                <Box mb={2}>
                  {loading
                    ? (
                      <Typography variant='caption'>
                        <CircularProgress size={10} /> Checking promo code...
                      </Typography>)
                    : (
                      <Typography variant='caption' sx={price?.next?.discount !== '0.00' ? sx.promoText : sx.promoError}>
                        {price?.next?.discount > 0 ? 'Promo code applied successfully!' : 'Invalid promo code. Please check and try again.'}
                      </Typography>
                      )}

                </Box>
              )}

              <Divider sx={{ mb: 2 }} />

              {/* Plan Details */}
              <Box display='flex' justifyContent='space-between' fontWeight='bold'>
                <Typography className={sx.heading} variant='body2'>PLAN</Typography>
                <Typography className={sx.heading} variant='body2'>PRICE</Typography>
              </Box>

              <Box display='flex' justifyContent='space-between' mt={1}>
                <PriceBox
                  label={price?.next?.discount > 0 ? plan?.name + ` (${coupon} Discount)` : plan?.name}
                  price={price?.next?.total}
                  isLoading={isLoading}
                  currency={plan?.price?.currency}
                />
              </Box>

              {(price && plan?.trialLength > 0) && (
                <Box display='flex' justifyContent='space-between' mt={1}>
                  <PriceBox
                    label={`${getFormattedDuration(plan.trialUnit, plan?.trialLength)} trial`}
                    price={(price?.next?.total * -1)}
                    currency={plan?.price?.currency}
                    className={sx.trialPrice}
                  />
                </Box>
              )}
              {(plan && price?.now?.items[0]?.setupFee > 0) && (
                <Box display='flex' justifyContent='space-between' mt={1}>
                  <PriceBox
                    label='Setup Fee'
                    price={price?.now?.items[0]?.setupFee}
                    currency={plan?.price?.currency}
                  />
                </Box>
              )}

              <Box display='flex' justifyContent='space-between' mt={1}>
                <Typography variant='body2'>{plan?.description}</Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display='flex' justifyContent='space-between' alignItems='center'>
                <PriceBox
                  label='Total'
                  className={sx.totalLabel}
                  price={price?.now?.total}
                  isLoading={isLoading}
                  currency={plan?.price?.currency}
                />
              </Box>

            </Box>
            <Box className={sx.terms}>
              <Typography variant='body2'>
                By <strong>completing payment</strong>, you agree to the Mindtools&nbsp;
                <TermsOfServiceLink />
            &nbsp;Cancel your subscription anytime through your payment and plan settings.
              </Typography>
            </Box>
            <Box className={sx.secured}>
              <Box display='flex' alignItems='center'>
                <LockIcon sx={{ color: 'gray', mr: 1 }} />
                <Typography variant='body2' color='textSecondary'>
                  Secured with SSL
                </Typography>
                <img width='93' src={PCILogo} alt='PCI compliant' />
              </Box>
            </Box>
          </Box>
        </>
      )}
    </>
  )
}

export default OrderSummary
